import React, { useState, useCallback, useContext, createContext } from 'react';
import api from 'services';
import { useFazenda } from 'hooks/providers/cadastros/fazendas';
import { toDate } from 'utils/converters';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

const VALORES_FINANCEIRO = {
  despesa: 0,
  receita: 0,
  total: 0,
};

const getReceitaDespesaDateValue = (item) => {
  const dateValue = item?.dataPagamento || item?.data_formatada || item?.data;

  if (!dateValue) return 0;

  const normalizedDate =
    typeof dateValue === 'string' && dateValue.length === 10
      ? `${dateValue}T00:00:00`
      : dateValue;

  const timestamp = new Date(normalizedDate).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortReceitasDespesasByDateAsc = (items) => {
  if (!Array.isArray(items)) return items;

  return [...items].sort(
    (a, b) => getReceitaDespesaDateValue(a) - getReceitaDespesaDateValue(b),
  );
};

const ReceitaDespesaContext = createContext({});

export function ReceitaDespesaProvider({ children }) {
  const [receitasDespesas, setReceitasDespesas] = useState([]);
  const [paginationFinanceiro, setPaginationFinanceiro] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [receitaDespesa, setReceitaDespesa] = useState({});
  const [valoresFinanceiro, setValoresFinanceiro] =
    useState(VALORES_FINANCEIRO);

  const [receitasDespesasLoading, setReceitasDespesasLoading] = useState(false);
  const [receitaDespesaLoading, setReceitaDespesaLoading] =
    React.useState(false);
  const [updatePagamentoLoading, setUpdatePagamentoLoading] = useState(false);

  const [editReceitaDespesaLoading, setEditReceitaDespesaLoading] =
    React.useState(false);
  const [insertReceitaDespesaLoading, setInsertReceitaDespesaLoading] =
    useState(false);
  const [addPagamentoLoading, setAddPagamentoLoading] = useState(false);
  const [changeStatusPagamentoLoading, setChangeStatusPagamentoLoading] =
    React.useState(false);

  const [resumo, setResumo] = React.useState({});
  const [resumoItem, setResumoItem] = React.useState([]);
  const [resumoLoading, setResumoLoading] = React.useState([]);

  const [deleteReceitaDespesaLoading, setDeleteReceitaDespesaLoading] =
    React.useState(false);

  const [deletePagamentoLoading, setDeletePagamentoLoading] =
    React.useState(false);
  const [pagamentoItemLoading, setPagamentoItemLoading] = React.useState(false);
  const [pagamentoItem, setPagamentoItem] = React.useState({});

  const [resumoTresMeses, setResumoTresMeses] = React.useState([]);
  const [resumoTresMesesLoading, setResumoTresMesesLoading] =
    React.useState(false);

  const { showToast } = useNewToast();

  const { fazendaAtiva } = useFazenda();

  const getReceitasDespesas = useCallback(async (filter) => {
    try {
      setReceitasDespesasLoading(true);
      const response = await api.financeiro().getReceitasDespesas(filter);
      setValoresFinanceiro({
        receita: response?.receita,
        despesa: response?.despesa,
        total: response?.total,
      });
      setReceitasDespesas(response?.container ?? []);
      setPaginationFinanceiro(
        response?.pagination ?? {
          page: 1,
          pageSize: 10,
          total: 0,
        },
      );
      setReceitasDespesasLoading(false);
    } catch (_) {
      setReceitasDespesasLoading(false);
    }
  }, []);

  const getReceitaDespesa = useCallback(async (filter) => {
    try {
      setReceitaDespesaLoading(true);
      const response = await api.financeiro().getReceitaDespesa(filter);
      setReceitaDespesa(response);
      setReceitaDespesaLoading(false);
    } catch (errors) {
      setReceitaDespesaLoading(false);
    }
  }, []);

  const getPagamentoItem = useCallback(async (id) => {
    try {
      setPagamentoItemLoading(true);
      const response = await api.financeiro().getPagamentoItem(id);
      setPagamentoItem(response);
      setPagamentoItemLoading(false);
    } catch (_) {
      setPagamentoItemLoading(false);
    }
  }, []);

  const insertReceitaDespesa = useCallback(
    async (value, movi) => {
      try {
        setInsertReceitaDespesaLoading(true);
        const {
          descricao,
          categoria,
          categoriaPagamento,
          tags,
          status,
          valor,
          tipoPagamento,
          safra,
          fornecedor,
          notafiscal,
          data,
          juros,
          desconto,
          dataPagamento,
          boleto,
          obsCheque,
          fluxo,
          fluxoSaida,
          numeroCheque,
          pagParcelas,
          fazenda,
          checkRateio,
          listaRateios,
          frequenciaPagamento,
        } = value.dados;

        const obsChequeValue = obsCheque?.trim() || '';

        const newData = {
          descricao,
          tipo: movi,
          categoria: categoria?.id,
          categoriaPagamento: categoriaPagamento?.id,
          tags:
            tags
              ?.map((item) => Number(item?.id ?? item))
              ?.filter((item) => !Number.isNaN(item)) || [],
          status,
          juros,
          desconto,
          valor,
          tipoPagamento,
          safra: safra?.id,
          fornecedor: fornecedor?.id,
          notafiscal,
          boleto,
          numeroCheque,
          data: toDate(data),
          dataPagamento: dataPagamento ? toDate(dataPagamento) : toDate(data),
          fluxo: fluxo?.id,
          fluxoSaida: fluxoSaida?.id,
          pagParcelas: Number(pagParcelas?.parcela) || 1,
          frequenciaPagamento:
            tipoPagamento === 'parcelado' || tipoPagamento === 'recorrente'
              ? frequenciaPagamento?.id || 'mensal'
              : 'mensal',
          obsCheque: obsChequeValue,
          pagamento_obs: obsChequeValue,
          files: value?.files,
          obs: value?.obs || '',
          fazenda: fazenda?.id || fazendaAtiva?.id,
        };

        if (checkRateio && listaRateios?.length > 0) {
          newData.rateios = listaRateios.map((rateio) => {
            const porcentagem = parseFloat(
              String(rateio.porcentagem || '0').replace(',', '.'),
            );

            return {
              gloSafraId: rateio.safra?.id,
              ...(rateio.talhao?.id && { gloTalhaoId: rateio.talhao.id }),
              percentualRateio: Number.isNaN(porcentagem) ? 0 : porcentagem,
            };
          });
        }

        // Devolvido para quem precisa do id recém-criado — hoje, o extrato do
        // banco, que amarra a movimentação ao lançamento logo depois de salvar.
        const criado = await api.financeiro().insertReceitaDespesa(newData);
        showToast({
          type: 'success',
          message: 'Lançamento adicionado com sucesso!',
        });
        setInsertReceitaDespesaLoading(false);
        return criado;
      } catch (err) {
        setInsertReceitaDespesaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [fazendaAtiva, showToast],
  );

  const editReceitaDespesa = useCallback(
    async (id, value) => {
      try {
        setEditReceitaDespesaLoading(true);
        const {
          tipo,
          categoria,
          categoriaPagamento,
          tags,
          safra,
          fornecedor,
          notafiscal,
          boleto,
          descricao,
          fazenda,
          checkRateio,
          listaRateios,
        } = value.dados;

        const newData = {
          descricao,
          tipo: tipo?.id,
          categoria: categoria?.id,
          categoriaPagamento: categoriaPagamento?.id,
          tags:
            tags
              ?.map((item) => Number(item?.id ?? item))
              ?.filter((item) => !Number.isNaN(item)) || [],
          safra: safra?.id,
          fornecedor: fornecedor?.id,
          notafiscal,
          obs: value?.obs || '',
          files: value?.files,
          boleto,
          fazenda: fazenda?.id || 0,
        };

        if (checkRateio && listaRateios?.length > 0) {
          newData.rateios = listaRateios.map((rateio) => {
            const porcentagem = parseFloat(
              String(rateio.porcentagem || '0').replace(',', '.'),
            );

            return {
              gloSafraId: rateio.safra?.id,
              ...(rateio.talhao?.id && { gloTalhaoId: rateio.talhao.id }),
              percentualRateio: Number.isNaN(porcentagem) ? 0 : porcentagem,
            };
          });
        } else {
          newData.rateios = [];
        }

        await api.financeiro().editReceitaDespesa(id, newData);
        showToast({
          type: 'success',
          message: 'Lançamento editado com sucesso!',
        });
        setEditReceitaDespesaLoading(false);
      } catch (err) {
        setEditReceitaDespesaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const updatePagamento = useCallback(
    async (idMovimentacao, value) => {
      try {
        setUpdatePagamentoLoading(true);
        const {
          fluxo,
          fluxoSaida,
          status,
          juros,
          desconto,
          valor,
          data,
          numeroCheque,
          obsCheque,
          notafiscal,
          dataPagamento,
          reajuste,
          checkCheque,
        } = value.pagamento;

        const newData = {
          fluxo: fluxo?.id || 0,
          fluxoSaida: fluxoSaida?.id || 0,
          status,
          juros,
          desconto,
          valor,
          notafiscal,
          data: toDate(data),
          file_id: value?.file_id,
          numeroCheque: checkCheque ? numeroCheque : '0',
          pagamento_obs: checkCheque ? obsCheque || '' : '',
          obs_parcela: value?.obs || '',
          dataPagamento: toDate(dataPagamento),
          reajuste: !reajuste?.id ? 0 : reajuste?.id,
        };
        const response = await api
          .financeiro()
          .updatePagamento(idMovimentacao, newData);

        const financeiroDetalhe = await api
          .financeiro()
          .getReceitaDespesa(response.gloFinanceiroId);
        setReceitaDespesa(financeiroDetalhe);

        const pagamentosById = new Map(
          (financeiroDetalhe?.pagamentos || []).map((parcela) => [
            Number(parcela.id),
            parcela,
          ]),
        );
        getReceitaDespesa(response.gloFinanceiroId);

        let nextList = [];
        setReceitasDespesas((old) => {
          nextList = sortReceitasDespesasByDateAsc(
            old.map((item) => {
              if (Number(item.id) === Number(value.pagamento.id)) {
                return {
                  ...response,
                  id: response.id,
                  descricao: item.descricao,
                  atual: response.atual,
                  categoria_descricao: item.categoria_descricao,
                  categoria_id: item.categoria_id ?? 1,
                  notafiscal,
                  colaborador: response.gloVinculoId,
                  data_formatada: response.dataPagamento || response.data,
                  valor: Number(response.valor),
                  tipo: item.tipo,
                  juros: Number(response.juros),
                  desconto: Number(response.desconto ?? item.desconto ?? 0),
                  status: response.status ?? item.status,
                  glo_financeiro_id: response.gloFinanceiroId,
                  referencia_tipo: response.referenciaTipo || '-',
                  referencia_id: response.referenciaId || 0,
                  obs: item.obs,
                  obs_parcela: value?.obs ?? '',
                  pagamento_obs: checkCheque
                    ? value?.pagamento?.obsCheque ?? ''
                    : '',
                };
              }

              const parcelaAtualizada = pagamentosById.get(Number(item.id));
              if (!parcelaAtualizada) {
                return item;
              }

              return {
                ...item,
                valor: Number(parcelaAtualizada.valor),
                juros: Number(parcelaAtualizada.juros),
                desconto: Number(parcelaAtualizada.desconto),
                status: parcelaAtualizada.status,
                data: parcelaAtualizada.data,
                data_formatada: parcelaAtualizada.data_formatada,
                dataPagamento: parcelaAtualizada.data_formatada,
              };
            }),
          );
          return nextList;
        });

        const receita = nextList.reduce(
          (total, item) =>
            item.tipo === 'areceber'
              ? total +
                Number(item.valor) +
                Number(item.juros) -
                Number(item.desconto)
              : total,
          0,
        );
        const despesa = nextList.reduce(
          (total, item) =>
            item.tipo === 'apagar'
              ? total +
                Number(item.valor) +
                Number(item.juros) -
                Number(item.desconto)
              : total,
          0,
        );
        setValoresFinanceiro({
          receita,
          despesa,
          total: receita - despesa,
        });

        showToast({
          type: 'success',
          message: 'Pagamento editado com sucesso!',
        });
        setUpdatePagamentoLoading(false);
        return response;
      } catch (err) {
        setUpdatePagamentoLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getReceitaDespesa],
  );

  const addPagamento = useCallback(
    async (id) => {
      setAddPagamentoLoading(true);
      try {
        const response = await api
          .financeiro()
          .addPagamento({ financeiroId: id });
        getReceitaDespesa(id);
        showToast({
          type: 'success',
          message: 'Pagamento adicionado com sucesso!',
        });
        setAddPagamentoLoading(false);
        return response;
      } catch (err) {
        setAddPagamentoLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getReceitaDespesa],
  );

  const getResumo = useCallback(async (filter) => {
    try {
      setResumoLoading(true);
      const response = await api.financeiro().getResumo(filter);
      setResumoItem(response.content);
      setResumo(response);
      setResumoLoading(false);
      return response;
    } catch (errors) {
      setResumoLoading(false);
      return null;
    }
  }, []);

  const getResumoTresMeses = useCallback(async (filter) => {
    try {
      setResumoTresMesesLoading(true);
      const response = await api.financeiro().getResumoTresMeses(filter);

      setResumoTresMeses(response);
      setResumoTresMesesLoading(false);
      return response;
    } catch (errors) {
      setResumoTresMesesLoading(false);
      return null;
    }
  }, []);

  const changeStatusPagamento = useCallback(
    async (id, value) => {
      const { status } = value;

      setChangeStatusPagamentoLoading(id);
      try {
        const response = await api.financeiro().changeStatus(id, { status });
        setReceitasDespesas((old) =>
          old.map((item) =>
            item.id === id
              ? {
                  ...item,
                  ...response,
                  valor: Number(item?.valor),
                  juros: Number(item?.juros),
                  desconto: Number(item?.desconto),
                }
              : item,
          ),
        );
        setTimeout(() => setChangeStatusPagamentoLoading(null), 1000);
        showToast({
          type: 'success',
          message: 'Pagamento alterado com sucesso!',
        });
      } catch (err) {
        setChangeStatusPagamentoLoading(null);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteReceitaDespesa = useCallback(
    async (id) => {
      try {
        setDeleteReceitaDespesaLoading(true);
        await api.financeiro().deleteReceitaDespesa(id);
        showToast({
          type: 'success',
          message: 'Movimentação apagada com sucesso!',
        });
        setDeleteReceitaDespesaLoading(false);
      } catch (err) {
        setDeleteReceitaDespesaLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deletePagamento = useCallback(
    async (idPagamento, idLancamento) => {
      try {
        setDeletePagamentoLoading(true);
        await api.financeiro().deletePagamento(idPagamento);
        getReceitaDespesa(idLancamento);
        showToast({
          type: 'success',
          message: 'Parcela apagada com sucesso!',
        });
      } catch (err) {
        setDeletePagamentoLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, getReceitaDespesa],
  );

  const [downloadCsvFinancialLoading, setDownloadCsvFinancialLoading] =
    useState(false);

  const downloadCsvFinancial = useCallback(
    async (filters) => {
      try {
        setDownloadCsvFinancialLoading(true);
        const response = await api.financeiro().downloadCsvFinancial(filters);
        const fileData = response.data;

        const blob = new Blob([fileData], { type: fileData.type });

        const now = new Date();
        const formattedDate = format(now, 'dd-MM-yyyy-HH-mm-ss');

        const fileName = `financeiro-${formattedDate}`;

        saveAs(blob, fileName);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDownloadCsvFinancialLoading(false);
      }
    },
    [showToast],
  );

  const contextValue = React.useMemo(
    () => ({
      receitasDespesasLoading,
      getReceitasDespesas,
      receitasDespesas,
      paginationFinanceiro,
      insertReceitaDespesa,
      receitaDespesaLoading,
      insertReceitaDespesaLoading,
      editReceitaDespesaLoading,
      editReceitaDespesa,
      updatePagamento,
      updatePagamentoLoading,
      valoresFinanceiro,
      getReceitaDespesa,
      receitaDespesa,
      resumoLoading,
      resumoItem,
      resumo,
      getResumo,
      pagamentoItem,
      setPagamentoItem,
      getPagamentoItem,
      pagamentoItemLoading,
      changeStatusPagamento,
      changeStatusPagamentoLoading,
      deletePagamentoLoading,
      deletePagamento,
      addPagamento,
      addPagamentoLoading,
      deleteReceitaDespesa,
      deleteReceitaDespesaLoading,
      getResumoTresMeses,
      resumoTresMeses,
      resumoTresMesesLoading,
      downloadCsvFinancial,
      downloadCsvFinancialLoading,
    }),
    [
      receitasDespesasLoading,
      getReceitasDespesas,
      receitasDespesas,
      paginationFinanceiro,
      insertReceitaDespesa,
      receitaDespesaLoading,
      insertReceitaDespesaLoading,
      editReceitaDespesaLoading,
      editReceitaDespesa,
      updatePagamento,
      updatePagamentoLoading,
      valoresFinanceiro,
      getReceitaDespesa,
      receitaDespesa,
      resumoLoading,
      resumoItem,
      resumo,
      getResumo,
      pagamentoItem,
      setPagamentoItem,
      getPagamentoItem,
      pagamentoItemLoading,
      changeStatusPagamento,
      changeStatusPagamentoLoading,
      deletePagamentoLoading,
      deletePagamento,
      addPagamento,
      addPagamentoLoading,
      deleteReceitaDespesa,
      deleteReceitaDespesaLoading,
      getResumoTresMeses,
      resumoTresMeses,
      resumoTresMesesLoading,
      downloadCsvFinancial,
      downloadCsvFinancialLoading,
    ],
  );

  return (
    <ReceitaDespesaContext.Provider value={contextValue}>
      {children}
    </ReceitaDespesaContext.Provider>
  );
}
export function useReceitaDespesa() {
  const context = useContext(ReceitaDespesaContext);

  if (!context)
    throw new Error('useReceitaDespesa must be used within an AuthProvider');

  return context;
}
